import { PaymentStatus } from '__gql-generated__/types'
import type { User } from 'api/data.types'

/**
 * Checks if user has access to the platform based on payment status
 */
export const hasAccessToPlatform = (user: User): boolean => {
  const { reader } = user

  if (!reader?.paymentStatus) {
    return !user.requiresPayment
  }

  return [
    PaymentStatus.Paid,
    PaymentStatus.Renewed,
    PaymentStatus.CanRenew,
    PaymentStatus.NotRequired
  ].includes(reader.paymentStatus)
}

/**
 * Checks if user needs to be redirected to payment page
 */
export const shouldRedirectToPayment = (user: User): boolean => {
  const { reader } = user

  if (!reader?.paymentStatus) {
    return user.requiresPayment || false
  }

  return [PaymentStatus.NotPaid, PaymentStatus.Pending].includes(
    reader.paymentStatus
  )
}

/**
 * Checks if user should be redirected to bookfair page
 * @param user
 * @returns true if user should be redirected to bookfair page, false otherwise
 */
export const shouldRedirectToBookfair = (user: User): boolean => {
  const { reader } = user

  if (
    !reader?.paymentStatus ||
    reader.entity?.contractInfo?.type !== 'fp_bookfair'
  ) {
    return false
  }

  return reader.paymentStatus === PaymentStatus.NotPaid
}

/**
 * Checks if user should see renewal banner
 */
export const shouldShowRenewalBanner = (user: User): boolean => {
  const { reader } = user

  if (!reader?.paymentStatus) {
    return false
  }

  return [PaymentStatus.CanRenew].includes(reader.paymentStatus)
}

/**
 * Checks if user has pending payment
 */
export const hasPendingPayment = (user: User): boolean => {
  const { reader } = user

  if (!reader?.paymentStatus) {
    return user.requiresPayment || false
  }

  return reader.paymentStatus === PaymentStatus.Pending
}

/**
 * Checks if user is fully paid (no payment required or already paid)
 */
export const isFullyPaid = (user: User): boolean => {
  const { reader } = user

  if (!reader?.paymentStatus) {
    return !user.requiresPayment
  }

  return [
    PaymentStatus.Paid,
    PaymentStatus.Renewed,
    PaymentStatus.NotRequired
  ].includes(reader.paymentStatus)
}
